import { getObfuscatedPhoneNumber } from '../../../../../util/account';
import { maskEmail } from '../../../../../util/email';
import { AUTH_TYPE } from '../../../../../constants/auth';
import { ACCOUNT_TYPE } from '../../AlbumFolder/AlbumFolders/AlbumFolderMembers/components/AlbumFolderIdentityMembershipDetails/constants';

/**
 * Flattens a list of accounts into the available authentication options,
 * producing one option per email address (accounts can have multiple) plus an
 * option for the phone number when present. Each option carries the obfuscated
 * display value used by the UI. Phone options lead the list and are flagged as
 * preferred whenever there is an email option to prefer them over.
 *
 * @param {Array<Object>} accounts The accounts associated with a QR code.
 * @returns {Array<{ key: String, account: Object, method: String, emailAddress?: String, phoneNumber?: String, label: String, value: String, preferred: Boolean }>}
 */
const getAccountAuthOptions = (accounts = []) => {
  const phoneFirstAccounts = [
    ...accounts.filter(account => !!account?.phone_number),
    ...accounts.filter(account => !account?.phone_number),
  ];

  const options = phoneFirstAccounts.reduce((accumulated, account) => {
    if (account.phone_number) {
      accumulated.push({
        key: `${account.id}-${AUTH_TYPE.PHONE}`,
        account,
        method: AUTH_TYPE.PHONE,
        phoneNumber: account.phone_number,
        label: 'PHONE',
        value: getObfuscatedPhoneNumber(account.phone_number),
      });
    }

    (account.email_addresses ?? []).forEach(emailAddress => {
      if (!emailAddress) {
        return;
      }

      accumulated.push({
        key: `${account.id}-${AUTH_TYPE.EMAIL}-${emailAddress}`,
        account,
        method: AUTH_TYPE.EMAIL,
        emailAddress,
        label: 'EMAIL',
        value: maskEmail(emailAddress),
      });
    });

    return accumulated;
  }, []);

  const hasEmailOption = options.some(
    option => option.method === AUTH_TYPE.EMAIL,
  );

  return options.map(option => ({
    ...option,
    preferred: hasEmailOption && option.method === AUTH_TYPE.PHONE,
  }));
};

/**
 * Picks the account a freshly authenticated identity has to be linked to. Only
 * marketing accounts are linkable, standard accounts are the identity itself.
 *
 * @param {Array<Object>} accounts The accounts associated with a QR code.
 * @returns {Object|undefined} The first marketing account, when there is one.
 */
const getLinkableAccount = (accounts = []) =>
  accounts.find(account => account?.type === ACCOUNT_TYPE.MARKETING);

export { getAccountAuthOptions, getLinkableAccount };
